import { saveReturnToProperty } from "@utils/postAuthRedirect";
import React, { useEffect, useState } from "react";
import "./PriceCard.css";
import { useNavigate } from "react-router-dom";
import { CycleToInterval } from "@utils/constants";
import { Modal } from "@shared/controls/Modal";
import { useSessionStorage } from "../../../hooks";
import ktoast from "@services/toast.service";
import {
  initiateBooking,
  getSinglePropertyTransaction,
  getSingleListingProperty,
} from "@services/krent.service";
import { Link, useLocation } from "react-router-dom";
import "react-datepicker/dist/react-datepicker.css";
import {
  collection,
  serverTimestamp,
  Timestamp,
  setDoc,
  doc,
} from "firebase/firestore";
import customFetch from "@services/axio";
import { firestore } from "@services/firebase.service";
import { formatCurrency, formatDate } from "@utils/helpers";
import PeakPeriod from "./PeakPeriod";
import BuyerCalPicker from "./buyer-cal-picker";
import { toast, ToastContainer } from "react-toastify";
import { LoginModal } from "../../LoginModal";
import { GetStartedModal } from "../../GetStartedModal";
import { shouldShowGuestProfileForm } from "@utils/guestProfileForm";
import CalendlyIcon from "../../../assets/calendly-icon.svg";
import PaymentMethodModal, {
  StartedRentBooking,
} from "../../PaymentMethodModal";
import {
  getRentBill,
  getRentPaymentTransaction,
} from "@utils/rentPaymentTransaction";
import { formatAvailableFrom, rentedOutUntil } from "@utils/rentStatus";

export interface DateRange {
  start: Date;
  end: Date;
}

type Props = {
  id: string;
  owner: string;
  currency: string;
  price: number;
  type: "long lease" | "sale" | "shortlet";
  duration: string;
  ownerName?: string;
  virtualTour?: string;
  cautionFee?: string;
  partyCautionFee?: string;
  status?: string;
  role?: string;
  partyPrice?: string | number;
  propertyInfo?: any;
};

export const PriceCard: React.FC<Props> = ({
  id,
  owner,
  ownerName,
  price,
  type,
  duration,
  currency,
  virtualTour,
  cautionFee,
  partyCautionFee,
  status,
  role,
  partyPrice,
  propertyInfo,
}: Props) => {
  const [user] = useSessionStorage("user", {});
  const location = useLocation();
  const [loading, setLoading] = useState<boolean>(false);
  const [dateFrom, setDateFrom] = useState<any | null>(null);
  const [dateTo, setDateTo] = useState<any | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalType, setModalType] = useState<string>("login");
  const [showLoginModal, setShowLoginModal] = useState<boolean>(false);
  const [showSignupModal, setShowSignupModal] = useState<boolean>(false);
  const [success, setSuccess] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [kyc, setKyc] = useState<any>("");
  const handleSignUpClick = () => {
    sessionStorage.setItem(
      "redirectAfterSignup",
      location.pathname + location.search,
    );
  };
  const navigate = useNavigate();
  const [normalDays, setNormalDays] = useState(0);
  const [normalPrice, setNormalPrice] = useState(0);
  const [normalDateRanges, setNormalDateRanges] = useState<DateRange[]>([]);
  const [surgeDays, setSurgeDays] = useState<{ date: string; price: number }[]>(
    [],
  );
  const [surgeDateRanges, setSurgeDateRanges] = useState<DateRange[]>([]);
  const [surgePrice, setSurgePrice] = useState(0);
  const [isPartyPrice, setIsPartyPrice] = useState<boolean>(false);
  const [showGuestsDropdown, setShowGuestsDropdown] = useState<boolean>(false);
  const [guests, setGuests] = useState({
    adults: 0,
    children: 0,
    infants: 0,
    pets: 0,
  });

  // Long-lease agent referral flow
  type AgentStep = "question" | "code-form" | "details";
  const [agentStep, setAgentStep] = useState<AgentStep>("question");
  const [savedAgentReferral, setSavedAgentReferral] = useState<{
    code?: string;
    name?: string;
    phone?: string;
    email?: string;
    agencyName?: string;
  }>();
  const [agentReferralCode, setAgentReferralCode] = useState("");
  const [agentName, setAgentName] = useState("");
  const [agentPhone, setAgentPhone] = useState("");
  const [agentAgencyName, setAgentAgencyName] = useState("");
  const [agentEmail, setAgentEmail] = useState("");
  const [agentStepAnimating, setAgentStepAnimating] = useState(false);
  const [agentResolveLoading, setAgentResolveLoading] = useState(false);
  const [resolvedAgent, setResolvedAgent] = useState<{
    name: string;
    agency: string;
  } | null>(null);
  const [agentResolveError, setAgentResolveError] = useState("");

  // Long-lease action modals
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [rentTerm, setRentTerm] = useState(1);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [rentBookingLoading, setRentBookingLoading] = useState(false);
  const [rentBookingError, setRentBookingError] = useState("");
  const [rentBookingId, setRentBookingId] = useState<string>("");
  const [rentReferenceId, setRentReferenceId] = useState("");

  const [paymentBill, setPaymentBill] = useState<any>(null);
  const [pendingRentBooking, setPendingRentBooking] = useState<{
    listing: string;
    durationYears?: number;
    durationMonths?: number;
    tc: boolean;
    agentReferral?: object;
  } | null>(null);
  const [rentPricing, setRentPricing] = useState<any>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState("");
  const [quoteRetry, setQuoteRetry] = useState(0);
  const [showAdminFeeNote, setShowAdminFeeNote] = useState(false);
  const rentIsMonthly = propertyInfo?.cycle === "monthly";
  const letUntil = rentedOutUntil({ intent: type, rentHold: propertyInfo?.rentHold });
  const rentUnit = rentIsMonthly ? "month" : "year";
  useEffect(() => {
    if (type !== "long lease") return;
    const controller = new AbortController();
    setQuoteLoading(true);
    setQuoteError("");
    setRentPricing(null);
    getSingleListingProperty(
      id,
      controller.signal,
      rentTerm,
      rentIsMonthly ? "months" : "years",
    )
      .then((response: any) => {
        if (controller.signal.aborted) return;
        const pricing = response?.data?.rentPricing;
        if (response?.success === false || !pricing || pricing.total == null)
          throw new Error(
            response?.errors?.[0]?.message ||
              response?.message ||
              "Could not load rent pricing.",
          );
        setRentPricing(pricing);
        const quotedTerm = rentIsMonthly ? pricing.months : pricing.years;
        if (quotedTerm !== rentTerm) setRentTerm(quotedTerm);
      })
      .catch((err) => {
        if (!controller.signal.aborted)
          setQuoteError(err.message || "Could not load rent pricing.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setQuoteLoading(false);
      });
    return () => controller.abort();
  }, [id, type, rentTerm, rentIsMonthly, quoteRetry]);
  const maxRentTerm = rentIsMonthly
    ? rentPricing?.maxRentalMonths
    : rentPricing?.maxRentalYears;
  const rentPackage = {
    periodRent: rentIsMonthly
      ? rentPricing?.monthlyRent
      : rentPricing?.annualRent,
    cautionFee: rentPricing?.cautionFee,
    administrativeFee: rentPricing?.administrativeFee,
    serviceCharge:
      type === "long lease"
        ? rentPricing?.serviceChargeTotal
        : Number(propertyInfo?.service_charge || 0),
  };
  const totalPackage = rentPricing?.total;

  const switchAgentStep = (next: AgentStep) => {
    setAgentStepAnimating(true);
    setTimeout(() => {
      setAgentStep(next);
      setAgentStepAnimating(false);
    }, 280);
  };

  const resolveAgentCode = async (code: string) => {
    if (code.trim().length < 3) {
      setResolvedAgent(null);
      setAgentResolveError("");
      return;
    }
    setAgentResolveLoading(true);
    setResolvedAgent(null);
    setAgentResolveError("");
    try {
      const res = await customFetch.get(
        `/rent/agents/resolve?code=${encodeURIComponent(code.trim())}`,
      );
      const data = res?.data?.data || res?.data;
      setResolvedAgent({
        name:
          data?.name ||
          `${data?.firstname || ""} ${data?.lastname || ""}`.trim(),
        agency: data?.agency || data?.agencyName || data?.team || "",
      });
    } catch {
      setAgentResolveError("Agent code not found. Please check and try again.");
    } finally {
      setAgentResolveLoading(false);
    }
  };

  useEffect(() => {
    setResolvedAgent(null);
    setAgentResolveError("");
    if (agentReferralCode.trim().length < 3) return;
    const timer = setTimeout(() => {
      resolveAgentCode(agentReferralCode);
    }, 400);
    return () => clearTimeout(timer);
  }, [agentReferralCode]);

  const submitRentBooking = async (agentReferral?: {
    code?: string;
    name?: string;
    phone?: string;
    email?: string;
  }) => {
    if (!user || !user?.id) {
      saveReturnToProperty();
      setShowLoginModal(true);
      return;
    }
    if (!agreedToTerms) {
      setRentBookingError(
        "Please agree to the Terms & Conditions and Cancellation Policy to continue.",
      );
      return;
    }
    if (rentBookingLoading || quoteLoading || !rentPricing) return;
    setRentBookingLoading(true);
    setRentBookingError("");
    try {
      const payload: {
        listing: string;
        durationYears?: number;
        durationMonths?: number;
        tc: boolean;
        agentReferral?: object;
      } = {
        listing: id,
        ...(rentIsMonthly
          ? { durationMonths: rentPricing.months }
          : { durationYears: rentPricing.years }),
        tc: agreedToTerms,
      };
      if (agentReferral && Object.keys(agentReferral).length > 0) {
        payload.agentReferral = agentReferral;
      }
      if (rentBookingId) {
        const existing: any = await getSinglePropertyTransaction(
          rentBookingId,
          new AbortController().signal,
        );
        const existingBill = getRentBill(existing);
        if (!existingBill)
          throw new Error(
            "Your booking is saved, but its bill is unavailable. Open My Bookings to continue payment.",
          );
        setPaymentBill(existingBill);
        setShowPaymentModal(true);
        return;
      }
      setPendingRentBooking(payload);
      setPaymentBill(rentPricing);
      setShowPaymentModal(true);
    } catch (err: any) {
      const msg =
        err?.response?.data?.errors?.[0]?.message ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to submit booking. Please try again.";
      setRentBookingError(msg);
    } finally {
      setRentBookingLoading(false);
    }
  };

  const startRentBooking = async (
    paymentMethod: "paystack" | "manual_bank_transfer",
  ): Promise<StartedRentBooking> => {
    if (!pendingRentBooking)
      throw new Error("Please start your booking again.");
    const bookingRes = await customFetch.post("/rent/bookings", {
      ...pendingRentBooking,
      paymentMethod,
    });
    const { transactionId, referenceId } = getRentPaymentTransaction(
      bookingRes.data,
    );
    if (!transactionId) {
      throw new Error(
        "Your booking was created, but payment details are unavailable. Please open My Bookings to continue payment.",
      );
    }
    setRentBookingId(transactionId);
    setRentReferenceId(referenceId);
    const bill = getRentBill(bookingRes.data);
    if (bill) setPaymentBill(bill);
    return {
      transactionId,
      referenceId,
      checkout: bookingRes.data?.data?.checkout,
      bankDetails: bookingRes.data?.data?.bankDetails,
    };
  };

  const messageCollections: any = process.env["REACT_APP_FIREBASE_COLLECTION"];

  useEffect(() => {
    // Restore booking data from sessionStorage if available
    const restoreBooking = sessionStorage.getItem("restoreBooking");
    console.log(
      "PriceCard mounted, checking for restoreBooking:",
      restoreBooking,
    );
    if (restoreBooking) {
      try {
        const parsed = JSON.parse(restoreBooking);
        console.log("Parsed restoreBooking data:", parsed);
        if (parsed.dateFrom) {
          console.log(
            "Restoring dateFrom:",
            parsed.dateFrom,
            "as Date object:",
            new Date(parsed.dateFrom),
          );
          setDateFrom(new Date(parsed.dateFrom));
        }
        if (parsed.dateTo) {
          console.log(
            "Restoring dateTo:",
            parsed.dateTo,
            "as Date object:",
            new Date(parsed.dateTo),
          );
          setDateTo(new Date(parsed.dateTo));
        }
        if (parsed.guests) {
          console.log("Restoring guests:", parsed.guests);
          setGuests(parsed.guests);
        }
        // Clear the restore data after a short delay to allow both instances to restore
        setTimeout(() => {
          sessionStorage.removeItem("restoreBooking");
          console.log("Cleared restoreBooking from sessionStorage");
        }, 1000);
      } catch (e) {
        console.error("Failed to parse restore booking data", e);
      }
    } else {
      console.log("No restoreBooking data found in sessionStorage");
    }
  }, []);

  useEffect(() => {
    if (dateFrom && dateTo) {
      const start = new Date(dateFrom);
      const end = new Date(dateTo);

      const totalSurgePrice = surgeDays
        .filter((item) => {
          const d = new Date(item.date);
          return d >= start && d < end;
        })
        .reduce((acc, item) => acc + item.price, 0);

      setSurgePrice(totalSurgePrice);
    } else {
      setSurgePrice(0);
    }
  }, [dateFrom, dateTo, surgeDays]);

  useEffect(() => {
    if (dateFrom && dateTo) {
      const start = new Date(dateFrom);
      const end = new Date(dateTo);

      let normalDaysCount = 0;
      let currentNormalRange = null;
      const normalRanges: DateRange[] = [];

      const diffInDays = Math.ceil(
        (end.getTime() - start.getTime()) / (1000 * 3600 * 24),
      );

      for (let i = 0; i < diffInDays; i++) {
        const current = new Date(start);
        current.setDate(start.getDate() + i);

        normalDaysCount++;
        if (!currentNormalRange) {
          currentNormalRange = {
            start: new Date(current),
            end: new Date(current),
          };
          currentNormalRange.end.setDate(current.getDate() + 1);
        } else {
          currentNormalRange.end = new Date(current);
          currentNormalRange.end.setDate(current.getDate() + 1);
        }
      }

      if (currentNormalRange) normalRanges.push(currentNormalRange);

      setNormalDateRanges(normalRanges);
      setNormalDays(normalDaysCount);
      setNormalPrice(normalDaysCount * price);
    } else {
      setNormalDays(0);
      setNormalPrice(0);
      setNormalDateRanges([]);
    }
  }, [dateFrom, dateTo, price, surgeDays, surgeDateRanges]);
  const handleSurgeData = (
    days: { date: string; price: number }[],
    dateRanges: DateRange[],
  ) => {
    const unique = Array.from(
      new Map(days.map((item) => [item.date, item])).values(),
    );

    setSurgeDays(unique);
    setSurgeDateRanges(dateRanges);
  };

  // const handleKyc = () => {
  //   const isUserEmpty = Object.keys(user).length === 0;

  //   if (isUserEmpty) {
  //     try {
  //       localStorage.setItem(
  //         "postLoginRedirect",
  //         window.location.pathname + window.location.search,
  //       );
  //     } catch (e) {
  //       console.error(
  //         "Unable to access localStorage while setting redirect:",
  //         e,
  //       );
  //     }

  //     setModalType("login");
  //     setShowModal(true);

  //     return;
  //   }
  //   if (user && kyc.status === "APPROVED") {
  //     bookTour();
  //   } else {
  //     setModalType("kyc");
  //     setShowModal(true);
  //     setLoading(false);
  //   }
  // };
  const bookTour = () => {
    if (user && user?.id) {
      setLoading(true);

      const formattedDateFrom = formatDate(dateFrom);
      const formattedDateTo = formatDate(dateTo);

      initiateBooking({
        listing: id,
        arrival_date: formattedDateFrom,
        departure_date: formattedDateTo,
        usePartyPrice: isPartyPrice && true,
        guest: {
          adult: guests.adults,
          children: guests.children,
          infants: guests.infants,
          pets: guests.pets,
        },
      })
        .then((d: any) => {
          try {
            // Validate required IDs
            const userId = user?.id;
            const ownerId = owner;

            if (!userId || !ownerId) {
              ktoast.error(
                "Unable to create conversation. Please refresh and try again.",
              );
              return;
            }

            const docData = {
              meta: {
                request: {
                  id: d?.data?.id || "",
                  property: id || "",
                  request_type: type || "",
                  arrival_date: d?.data?.arrival_date || dateFrom || null,
                  departure_date: d?.data?.departure_date || dateTo || null,
                },
                sent_by: {
                  name:
                    user?.prefferredName ||
                    `${user?.firstname || ""} ${user?.lastname || ""}`,
                  photoUrl: user?.photo || "",
                },
                owner: {
                  name: ownerName || "",
                },
              },
              chats: [
                {
                  from_uid: userId,
                  to_uid: ownerId,
                  type: "text",
                  value: "I'm interested in your property",
                  createdAt: Timestamp.now(),
                },
              ],
              read: false,
              members: [ownerId, userId],
              createdAt: serverTimestamp(),
            };

            setDoc(
              doc(collection(firestore, messageCollections), d?.data?.id || ""),
              docData,
            ).then(() => {
              ktoast.success(
                "Your booking has been logged successfully. The Property owner will be notified of your interest.",
              );
              setSuccess(
                "Your booking has been logged successfully. The Property owner will be notified of your interest.",
              );
              setTimeout(() => {
                navigate(`/messaging/${d?.data.id}`);
              }, 4000);
            });
          } catch (e) {
            console.error("Error adding document: ", e);
          }
        })
        .catch((err: any) => {
          ktoast.error(err.errors?.[0]?.message || "An error occurred.");
          setError(err.errors?.[0]?.message || "An error occurred.");
          setTimeout(() => {
            setError("");
          }, 3000);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      saveReturnToProperty();
      setShowLoginModal(true);
      setLoading(false);
    }
  };

  const getKycStatus = async () => {
    try {
      const resp = await customFetch.get("/users/kyc");
      setKyc(resp?.data?.data);
    } catch (error: any) {
      setKyc(error?.response?.data?.errors[0]?.message);
    }
  };
  useEffect(() => {
    getKycStatus();
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".guests-dropdown-container")) {
        setShowGuestsDropdown(false);
      }
    };

    if (showGuestsDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showGuestsDropdown]);

  if (type === "long lease" && role && role !== "user") {
    return (
      <div className="border-1 rounded-4 shadow-sm border-purple mb-4 p-4 font-onest">
        <h5 className="fw-bold mb-4">Property price breakdown</h5>
        {[
          [
            rentIsMonthly ? "Monthly rent" : "Annual rent",
            rentPackage.periodRent,
          ],
          ["Caution fee", rentPackage.cautionFee],
          ["Administrative fee", rentPackage.administrativeFee],
          ["Service charge", rentPackage.serviceCharge],
        ].map(([label, amount]) => (
          <div
            key={label}
            className="d-flex justify-content-between gap-3 py-3 border-bottom"
          >
            <span>{label}</span>
            <span>
              {currency}
              {amount == null
                ? "Confirmed at checkout"
                : formatCurrency(Number(amount))}
            </span>
          </div>
        ))}
        <div className="d-flex justify-content-between gap-3 pt-4 fw-bold">
          <span>Total Package</span>
          <span>
            {currency}
            {totalPackage == null
              ? "Confirmed at checkout"
              : formatCurrency(totalPackage)}
          </span>
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <ToastContainer limit={1} />

      <div className="border-1 rounded-4 shadow-sm border-purple mb-4">
        <div className="d-flex flex-column p-4 pb-1 h-100">
          {type === "long lease" && quoteLoading && (
            <p role="status">Loading rent quote…</p>
          )}
          {type === "long lease" && quoteError && (
            <div role="alert" className="text-danger">
              {quoteError}{" "}
              <button
                type="button"
                className="btn btn-link"
                onClick={() => setQuoteRetry((value) => value + 1)}
              >
                Retry
              </button>
            </div>
          )}
          <div className="d-flex flex-column">
            {!(type === "long lease" && agentStep === "question") && (
              <>
                <div className="d-flex d-md-block d-lg-flex justify-content-between align-items-center">
                  <p className="fs-12 font-onest">
                    <span className="fs-22 fw-bolder font-onest">
                      {currency}
                      {type === "shortlet" && isPartyPrice
                        ? formatCurrency(partyPrice)
                        : formatCurrency(
                            propertyInfo.hasDailyPrice
                              ? propertyInfo.dailyPrice
                              : price,
                          )}
                    </span>
                    {type !== "sale" && (
                      <span> / {CycleToInterval(duration)}</span>
                    )}
                  </p>

                  <p className="m-0">
                    {type === "sale" && (
                      <span className="badge d-inline-flex fs-10 px-2 py-2 fw-semibold text-custom-royal bg-custom-royal bg-opacity-10 border border-success border-opacity-10 rounded-pill font-onest">
                        FOR SALE
                      </span>
                    )}
                    {type === "long lease" && (
                      <span className="badge d-inline-flex fs-10 px-2 py-2 fw-semibold text-success bg-success bg-opacity-10 border border-success border-opacity-10 rounded-pill font-onest">
                        RENTAL
                      </span>
                    )}
                  </p>
                </div>

                {type === "shortlet" && partyPrice && (
                  <div className="d-flex align-items-center items-center gap-1 mt-1">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input cursor-pointer"
                        type="checkbox"
                        id="partyPriceSwitch"
                        checked={isPartyPrice}
                        onChange={() => setIsPartyPrice(!isPartyPrice)}
                        style={{ marginTop: "6.5px" }}
                      />
                    </div>
                    <p className="mb-0 fw-semibold font-onest fs-12">
                      Switch to see{" "}
                      {!isPartyPrice ? "party price" : "normal price"}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          {!(type === "long lease" && agentStep === "question") && <hr />}
          {role !== "agent" && (
            <div className="">
              {/* ── Long-lease flow ────────────────────────────────────── */}
              {type === "long lease" ? (
                <div>
                  {success && (
                    <span className="fs-13 text-success">{success}</span>
                  )}
                  {error && <span className="fs-13 text-danger">{error}</span>}

                  {/* Animated step container */}
                  <div
                    style={{
                      overflow: "hidden",
                      transition: "opacity 0.28s ease, transform 0.28s ease",
                      opacity: agentStepAnimating ? 0 : 1,
                      transform: agentStepAnimating
                        ? "translateX(12px)"
                        : "translateX(0)",
                    }}
                  >
                    {/* Step 1 — referral question only */}
                    {agentStep === "question" && (
                      <div
                        className="border rounded-3 p-4 text-center mb-4"
                        style={{ borderColor: "#dee2e6" }}
                      >
                        <p
                          className="fw-semibold font-onest mb-1"
                          style={{ color: "#47BBA9", fontSize: "16px" }}
                        >
                          Were you referred by an agent?
                        </p>
                        <p className="fs-13 text-muted font-onest mb-3">
                          If an agent introduced you to us, let us know so we
                          can properly credit their referral.
                        </p>
                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn py-2 fw-semibold font-onest flex-fill rounded-3"
                            style={{
                              background: "#47BBA9",
                              color: "#fff",
                              border: "none",
                            }}
                            onClick={() => {
                              if (!user || !user?.id) {
                                saveReturnToProperty();
                                setShowLoginModal(true);
                                return;
                              }
                              switchAgentStep("code-form");
                            }}
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            className="btn py-2 fw-semibold font-onest flex-fill rounded-3"
                            style={{
                              background: "#fff",
                              color: "#47BBA9",
                              border: "1.5px solid #47BBA9",
                            }}
                            onClick={() => {
                              if (!user || !user?.id) {
                                saveReturnToProperty();
                                setShowLoginModal(true);
                                return;
                              }
                              setSavedAgentReferral(undefined);
                              switchAgentStep("details");
                            }}
                          >
                            No
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Step 2 — agent code form */}
                    {agentStep === "code-form" && (
                      <div
                        className="border rounded-3 p-4 mb-4"
                        style={{ borderColor: "#dee2e6" }}
                      >
                        <h6
                          className="fw-bold font-onest text-center mb-1"
                          style={{ color: "#47BBA9" }}
                        >
                          Enter Agent Code
                        </h6>
                        <p className="fs-13 text-muted text-center font-onest mb-3">
                          If an agent helped you find this property, enter their
                          unique code below.
                        </p>

                        <label className="fs-12 fw-semibold font-onest mb-1">
                          Agent Referral Code
                        </label>
                        <input
                          type="text"
                          className="form-control rounded-3 mb-2 fs-14"
                          placeholder="Agent Referral Code"
                          value={agentReferralCode}
                          onChange={(e) => {
                            setAgentReferralCode(e.target.value);
                          }}
                        />

                        {/* Resolved agent preview */}
                        {agentResolveLoading && (
                          <div className="d-flex align-items-center gap-2 mb-3">
                            <span
                              className="spinner-border spinner-border-sm text-primary"
                              role="status"
                            ></span>
                            <span className="fs-13 text-muted font-onest">
                              Looking up agent…
                            </span>
                          </div>
                        )}
                        {resolvedAgent && !agentResolveLoading && (
                          <div
                            className="d-flex align-items-start gap-2 rounded-3 p-3 mb-3"
                            style={{
                              background: "#FFFBEA",
                              border: "1px solid #F5E6A3",
                            }}
                          >
                            <span style={{ fontSize: "16px", flexShrink: 0 }}>
                              ℹ️
                            </span>
                            <div>
                              <p
                                className="mb-0 fw-semibold font-onest fs-13"
                                style={{ color: "#1a1a1a" }}
                              >
                                Agent Name: {resolvedAgent.name}
                              </p>
                              {resolvedAgent.agency && (
                                <p
                                  className="mb-0 fw-semibold font-onest fs-13"
                                  style={{ color: "#1a1a1a" }}
                                >
                                  Agency / Team: {resolvedAgent.agency}
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                        {agentResolveError && !agentResolveLoading && (
                          <p className="fs-13 text-danger font-onest mb-3">
                            {agentResolveError}
                          </p>
                        )}

                        <p className="fs-13 text-center font-onest mb-3">
                          Don't have a referral code?{" "}
                          <button
                            type="button"
                            className="btn btn-link p-0 fw-semibold"
                            style={{ color: "#47BBA9" }}
                            onClick={(event) => {
                              setAgentReferralCode("");
                              setResolvedAgent(null);
                              setAgentResolveError("");
                              event.currentTarget
                                .closest("p")
                                ?.parentElement?.querySelector<HTMLInputElement>(
                                  '[data-agent-name="true"]',
                                )
                                ?.focus();
                            }}
                          >
                            Enter agent details directly
                          </button>
                        </p>

                        <label className="fs-12 fw-semibold font-onest mb-1">
                          Agency name (optional)
                        </label>
                        <input
                          className="form-control rounded-3 mb-2 fs-14"
                          value={agentAgencyName}
                          onChange={(event) =>
                            setAgentAgencyName(event.target.value)
                          }
                          placeholder="Agency name"
                        />
                        <label className="fs-12 fw-semibold font-onest mb-1">
                          Agent Name*
                        </label>
                        <input
                          type="text"
                          className="form-control rounded-3 mb-2 fs-14"
                          data-agent-name="true"
                          placeholder="Agent Full Name"
                          value={agentName}
                          onChange={(e) => setAgentName(e.target.value)}
                        />

                        <label className="fs-12 fw-semibold font-onest mb-1">
                          Agent Phone Number*
                        </label>
                        <input
                          type="tel"
                          className="form-control rounded-3 mb-2 fs-14"
                          placeholder="Agent Phone Number"
                          value={agentPhone}
                          onChange={(e) => setAgentPhone(e.target.value)}
                        />

                        <label className="fs-12 fw-semibold font-onest mb-1">
                          Agent Email*
                        </label>
                        <input
                          type="email"
                          className="form-control rounded-3 mb-3 fs-14"
                          placeholder="Agent Email Address"
                          value={agentEmail}
                          onChange={(e) => setAgentEmail(e.target.value)}
                        />

                        <div
                          className={`rent-consent font-onest mb-3${agreedToTerms ? " rent-consent--checked" : ""}`}
                        >
                          <input
                            type="checkbox"
                            id="agentReferralTerms"
                            className="rent-consent__checkbox"
                            required
                            aria-describedby="agentReferralTermsRequired"
                            checked={agreedToTerms}
                            onChange={(e) => setAgreedToTerms(e.target.checked)}
                          />
                          <div className="rent-consent__content">
                            <label
                              htmlFor="agentReferralTerms"
                              className="rent-consent__label"
                            >
                              I agree to the{" "}
                              <a href="/terms" target="_blank" rel="noreferrer">
                                Terms &amp; Conditions
                              </a>{" "}
                              and{" "}
                              <a
                                href="/cancellation-policy"
                                target="_blank"
                                rel="noreferrer"
                              >
                                Cancellation Policy
                              </a>
                              .
                            </label>
                            <span
                              id="agentReferralTermsRequired"
                              className="rent-consent__hint"
                            >
                              Required to continue
                            </span>
                          </div>
                        </div>

                        <button
                          type="button"
                          className="btn py-3 w-100 fw-semibold font-onest rounded-3"
                          style={{
                            background: "#47BBA9",
                            color: "#fff",
                            border: "none",
                          }}
                          disabled={
                            !agreedToTerms ||
                            rentBookingLoading ||
                            quoteLoading ||
                            !rentPricing
                          }
                          onClick={() => {
                            const agentRef: {
                              code?: string;
                              name?: string;
                              phone?: string;
                              email?: string;
                              agencyName?: string;
                            } = {};
                            if (agentReferralCode.trim()) {
                              agentRef.code = agentReferralCode.trim();
                            } else {
                              if (agentAgencyName.trim())
                                agentRef.agencyName = agentAgencyName.trim();
                              if (agentName.trim())
                                agentRef.name = agentName.trim();
                              if (agentPhone.trim())
                                agentRef.phone = agentPhone.trim();
                              if (agentEmail.trim())
                                agentRef.email = agentEmail.trim();
                            }
                            setSavedAgentReferral(agentRef);
                            switchAgentStep("details");
                          }}
                        >
                          {rentBookingLoading ? (
                            <span
                              className="spinner-border spinner-border-sm"
                              role="status"
                              aria-hidden="true"
                            ></span>
                          ) : (
                            "Save & Continue"
                          )}
                        </button>
                        {rentBookingError && (
                          <p className="fs-13 text-danger font-onest mt-2 mb-0 text-center">
                            {rentBookingError}
                          </p>
                        )}

                        <p className="fs-12 text-center text-muted font-onest mt-2 mb-0">
                          Don't have all the details? You can{" "}
                          <span
                            className="fw-semibold"
                            style={{ color: "#47BBA9", cursor: "pointer" }}
                            onClick={() => switchAgentStep("question")}
                          >
                            go back
                          </span>{" "}
                          and change your selection.
                        </p>
                      </div>
                    )}

                    {/* Step 3 — full rental details */}
                    {agentStep === "details" && (
                      <div>
                        {/* Price breakdown */}
                        <div className="mb-3">
                          <div
                            className="d-flex justify-content-between align-items-center py-2"
                            style={{ borderBottom: "1px solid #f0f0f0" }}
                          >
                            <span
                              className="fs-14 font-onest"
                              style={{
                                textDecoration: "underline",
                                textUnderlineOffset: "3px",
                              }}
                            >
                              {rentTerm === 1
                                ? rentIsMonthly
                                  ? "Monthly Rent"
                                  : "Annual Rent"
                                : `Rent (${rentTerm} ${rentUnit}s)`}
                            </span>
                            <span className="fs-14 font-onest">
                              {currency}
                              {rentPricing
                                ? formatCurrency(rentPricing.rentTotal)
                                : "—"}
                            </span>
                          </div>
                          <div
                            className="d-flex justify-content-between align-items-center py-2"
                            style={{ borderBottom: "1px solid #f0f0f0" }}
                          >
                            <span
                              className="fs-14 font-onest"
                              style={{
                                textDecoration: "underline",
                                textUnderlineOffset: "3px",
                              }}
                            >
                              Caution fee
                            </span>
                            <span className="fs-14 font-onest">
                              {currency}
                              {rentPricing
                                ? formatCurrency(rentPricing.cautionFee)
                                : "—"}
                            </span>
                          </div>
                          <div
                            className="d-flex justify-content-between align-items-center py-2"
                            style={{ borderBottom: "1px solid #f0f0f0" }}
                          >
                            <span
                              className="fs-14 font-onest"
                              style={{
                                textDecoration: "underline",
                                textUnderlineOffset: "3px",
                              }}
                            >
                              Administrative fee
                              <button
                                type="button"
                                className="btn btn-link p-0 ms-1 align-baseline"
                                aria-label="What the administrative fee covers"
                                aria-expanded={showAdminFeeNote}
                                onClick={() =>
                                  setShowAdminFeeNote((open) => !open)
                                }
                                style={{
                                  fontSize: "14px",
                                  textDecoration: "none",
                                }}
                              >
                                ⓘ
                              </button>
                            </span>
                            <span className="fs-14 font-onest">
                              {rentPackage.administrativeFee != null &&
                                currency}
                              {rentPackage.administrativeFee == null
                                ? "Confirmed at checkout"
                                : formatCurrency(rentPackage.administrativeFee)}
                            </span>
                          </div>
                          {showAdminFeeNote &&
                            rentPricing?.administrativeFeeNote && (
                              <p
                                className="fs-13 font-onest text-muted mb-0 py-2"
                                role="note"
                              >
                                {rentPricing.administrativeFeeNote}
                              </p>
                            )}
                          <div
                            className="d-flex justify-content-between align-items-center py-2"
                            style={{ borderBottom: "1px solid #f0f0f0" }}
                          >
                            <span
                              className="fs-14 font-onest"
                              style={{
                                textDecoration: "underline",
                                textUnderlineOffset: "3px",
                              }}
                            >
                              Service charge
                            </span>
                            <span className="fs-14 font-onest">
                              {currency}
                              {formatCurrency(rentPackage.serviceCharge)}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between align-items-center pt-3 mt-1">
                            <span className="fw-bold font-onest fs-15">
                              Total Package
                            </span>
                            <span className="fw-bold font-onest fs-15">
                              {totalPackage != null && currency}
                              {totalPackage == null
                                ? "Confirmed at checkout"
                                : formatCurrency(totalPackage)}
                            </span>
                          </div>
                        </div>

                        {/* Yellow notice */}
                        <div
                          className="d-flex align-items-start gap-2 rounded-3 p-3 mb-4"
                          style={{
                            background: "#FFFBEA",
                            border: "1px solid #F5E6A3",
                          }}
                        >
                          <span style={{ fontSize: "16px", flexShrink: 0 }}>
                            ⚠️
                          </span>
                          <p
                            className="mb-0 fs-13 font-onest"
                            style={{ color: "#7a6200" }}
                          >
                            Your tenancy starts{" "}
                            {rentPricing?.commencementOffsetDays ?? "—"} days
                            after your payment is verified.
                          </p>
                        </div>
                        {/* Rental duration stepper */}
                        <div
                          className="border rounded-3 p-3 mb-4"
                          style={{ borderColor: "#dee2e6" }}
                        >
                          <p
                            className="text-uppercase fw-bold font-onest mb-2"
                            style={{
                              fontSize: "11px",
                              letterSpacing: "0.05em",
                            }}
                          >
                            Rental Duration
                          </p>
                          <div className="d-flex align-items-center justify-content-between">
                            <span className="font-onest fs-14 text-muted">
                              {rentIsMonthly
                                ? "Months of stay"
                                : "Years of stay"}
                            </span>
                            <div className="d-flex align-items-center gap-3">
                              <button
                                type="button"
                                aria-label={`Decrease ${rentUnit}s`}
                                onClick={() =>
                                  setRentTerm((y) => Math.max(1, y - 1))
                                }
                                disabled={
                                  rentTerm <= 1 ||
                                  !!rentBookingId ||
                                  rentBookingLoading ||
                                  quoteLoading
                                }
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: "50%",
                                  border: "1.5px solid #adb5bd",
                                  background: "transparent",
                                  cursor:
                                    rentTerm <= 1 ? "not-allowed" : "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  opacity: rentTerm <= 1 ? 0.4 : 1,
                                  transition: "opacity 0.2s",
                                  flexShrink: 0,
                                }}
                              >
                                <i
                                  className="fas fa-minus"
                                  style={{ fontSize: "10px" }}
                                ></i>
                              </button>
                              <span
                                className="fw-semibold font-onest"
                                style={{
                                  minWidth: "20px",
                                  textAlign: "center",
                                }}
                              >
                                {rentTerm}
                              </span>
                              <button
                                type="button"
                                aria-label={`Increase ${rentUnit}s`}
                                disabled={
                                  !!rentBookingId ||
                                  rentBookingLoading ||
                                  quoteLoading ||
                                  !rentPricing ||
                                  rentTerm >= maxRentTerm
                                }
                                onClick={() => setRentTerm((y) => y + 1)}
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: "50%",
                                  border: "1.5px solid #adb5bd",
                                  background: "transparent",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  transition: "opacity 0.2s",
                                  flexShrink: 0,
                                }}
                              >
                                <i
                                  className="fas fa-plus"
                                  style={{ fontSize: "10px" }}
                                ></i>
                              </button>
                            </div>
                          </div>

                          <div className="d-flex align-items-start gap-2 mt-3">
                            <input
                              type="checkbox"
                              id="longLeaseTerms"
                              className="mt-1"
                              checked={agreedToTerms}
                              onChange={(e) =>
                                setAgreedToTerms(e.target.checked)
                              }
                              style={{
                                accentColor: "#47BBA9",
                                cursor: "pointer",
                              }}
                            />
                            <label
                              htmlFor="longLeaseTerms"
                              className="fs-13 font-onest"
                              style={{ cursor: "pointer" }}
                            >
                              I agree to the{" "}
                              <a
                                href="/terms"
                                className="text-primary"
                                target="_blank"
                                rel="noreferrer"
                              >
                                Terms &amp; Conditions
                              </a>{" "}
                              and{" "}
                              <a
                                href="/cancellation-policy"
                                className="text-primary"
                                target="_blank"
                                rel="noreferrer"
                              >
                                Cancellation Policy
                              </a>
                            </label>
                          </div>
                        </div>

                        {/* Action buttons */}
                        <div className="d-flex flex-column gap-2 mb-2">
                          {letUntil && (
                            <p className="fs-13 text-danger font-onest mb-1 text-center">
                              This property is rented out. It will be available again from {formatAvailableFrom(letUntil)}.
                            </p>
                          )}
                          {rentBookingError && (
                            <p className="fs-13 text-danger font-onest mb-1 text-center">
                              {rentBookingError}
                            </p>
                          )}
                          <button
                            type="button"
                            className="btn py-3 fw-semibold font-onest rounded-4 w-100"
                            style={{
                              background: "#47BBA9",
                              color: "#fff",
                              border: "none",
                            }}
                            disabled={!!letUntil || !agreedToTerms || rentBookingLoading}
                            onClick={() =>
                              submitRentBooking(savedAgentReferral)
                            }
                          >
                            {rentBookingLoading ? (
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                              ></span>
                            ) : letUntil ? (
                              "Rented out"
                            ) : (
                              "Rent now"
                            )}
                          </button>
                          <button
                            type="button"
                            className="btn py-3 fw-semibold font-onest rounded-4 w-100"
                            style={{
                              background: "#fff",
                              color: "#47BBA9",
                              border: "1.5px solid #47BBA9",
                            }}
                            onClick={() => setShowInspectionModal(true)}
                          >
                            Book Inspection
                          </button>
                          <p className="text-center fs-12 text-muted mb-0">
                            You won't be charged yet
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                /* ── shortlet / sale flow (unchanged) ──────────────────── */
                <div>
                  <h5 className="fw-bold mb-2">
                    {type === "shortlet" ? "" : "Contact Owner"}
                  </h5>
                  {success && (
                    <span className="fs-13 text-success">{success}</span>
                  )}

                  {error && <span className="fs-13 text-danger">{error}</span>}
                  <BuyerCalPicker
                    type={type}
                    dateFrom={dateFrom}
                    setDateFrom={setDateFrom}
                    dateTo={dateTo}
                    setDateTo={setDateTo}
                    propertyId={id}
                    setSurgeData={handleSurgeData}
                    surgeDateRanges={surgeDateRanges}
                    surgeDays={surgeDays}
                    normalPrice={price}
                    partyPrice={partyPrice}
                    isPartyPrice={isPartyPrice}
                  />

                  {type === "shortlet" && (
                    <div className="mb-3 mt-3 guests-dropdown-container position-relative">
                      <div className="mb-3">
                        {/* Guests trigger — matches card style from BuyerCalPicker */}
                        <div
                          className="border rounded-4 overflow-hidden"
                          style={{ borderColor: "#dee2e6", cursor: "pointer" }}
                          onClick={() =>
                            setShowGuestsDropdown(!showGuestsDropdown)
                          }
                        >
                          <div className="d-flex align-items-center justify-content-between px-3 py-3">
                            <div className="flex-grow-1 min-w-0">
                              <div
                                className="text-uppercase fw-bold mb-1 font-onest"
                                style={{
                                  fontSize: "11px",
                                  letterSpacing: "0.05em",
                                  color: "#212529",
                                }}
                              >
                                Guests
                              </div>
                              <div
                                style={{ fontSize: "14px", color: "#495057" }}
                                className="font-onest"
                              >
                                {guests.adults + guests.children} guests
                                {guests.infants > 0 &&
                                  `, ${guests.infants} infants`}
                                {guests.pets > 0 && `, ${guests.pets} pets`}
                              </div>
                            </div>
                            <i
                              className={`fas fa-chevron-${showGuestsDropdown ? "up" : "down"}`}
                              style={{
                                color: "#6c757d",
                                fontSize: "14px",
                                flexShrink: 0,
                              }}
                            ></i>
                          </div>
                        </div>
                      </div>

                      {showGuestsDropdown && (
                        <div
                          className="position-absolute bg-white border rounded shadow-sm p-3"
                          style={{
                            zIndex: 1000,
                            left: 0,
                            right: 0,
                            width: "100%",
                            maxWidth: "100%",
                            boxSizing: "border-box",
                            border: "1px solid #E9ECEF",
                            borderRadius: "12px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-start mb-3 gap-2">
                            <div className="min-w-0 flex-grow-1">
                              <p className="mb-0 fw-semibold font-onest">
                                Guests
                              </p>
                              <p className="mb-0 text-muted small font-onest">
                                Max {propertyInfo?.guests || 0} guests
                                {propertyInfo?.pets > 0 &&
                                  ` • ${propertyInfo.pets} pets allowed`}
                              </p>
                            </div>
                            <button
                              type="button"
                              className="btn-close flex-shrink-0"
                              onClick={() => setShowGuestsDropdown(false)}
                            ></button>
                          </div>

                          <div className="border-top pt-3">
                            {/* Adults */}
                            <div className="d-flex justify-content-between align-items-center mb-3 gap-2">
                              <div className="min-w-0">
                                <div className="fw-semibold font-onest">
                                  Adults
                                </div>
                                <div className="text-muted small font-onest">
                                  Age 18+
                                </div>
                              </div>
                              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                <button
                                  type="button"
                                  disabled={guests.adults <= 1}
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    borderRadius: "50%",
                                    border: "1.5px solid #adb5bd",
                                    background: "transparent",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    cursor:
                                      guests.adults <= 1
                                        ? "not-allowed"
                                        : "pointer",
                                    opacity: guests.adults <= 1 ? 0.35 : 1,
                                    transition: "opacity 0.2s",
                                  }}
                                  onClick={() =>
                                    setGuests((prev) => ({
                                      ...prev,
                                      adults: Math.max(1, prev.adults - 1),
                                    }))
                                  }
                                >
                                  <i
                                    className="fas fa-minus"
                                    style={{ fontSize: "10px" }}
                                  ></i>
                                </button>
                                <span
                                  className="fw-semibold"
                                  style={{
                                    minWidth: "24px",
                                    textAlign: "center",
                                    flexShrink: 0,
                                  }}
                                >
                                  {guests.adults}
                                </span>
                                <button
                                  type="button"
                                  disabled={
                                    guests.adults + guests.children >=
                                    (propertyInfo?.guests || 99)
                                  }
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    borderRadius: "50%",
                                    border: "1.5px solid #adb5bd",
                                    background: "transparent",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    cursor:
                                      guests.adults + guests.children >=
                                      (propertyInfo?.guests || 99)
                                        ? "not-allowed"
                                        : "pointer",
                                    opacity:
                                      guests.adults + guests.children >=
                                      (propertyInfo?.guests || 99)
                                        ? 0.35
                                        : 1,
                                    transition: "opacity 0.2s",
                                  }}
                                  onClick={() =>
                                    setGuests((prev) => {
                                      const n = prev.adults + 1;
                                      return {
                                        ...prev,
                                        adults:
                                          n + prev.children <=
                                          (propertyInfo?.guests || 99)
                                            ? n
                                            : prev.adults,
                                      };
                                    })
                                  }
                                >
                                  <i
                                    className="fas fa-plus"
                                    style={{ fontSize: "10px" }}
                                  ></i>
                                </button>
                              </div>
                            </div>

                            {/* Children */}
                            <div className="d-flex justify-content-between align-items-center mb-3 gap-2">
                              <div className="min-w-0">
                                <div className="fw-semibold font-onest">
                                  Children
                                </div>
                                <div className="text-muted small font-onest">
                                  Ages 2-17
                                </div>
                              </div>
                              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                <button
                                  type="button"
                                  disabled={guests.children <= 0}
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    borderRadius: "50%",
                                    border: "1.5px solid #adb5bd",
                                    background: "transparent",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    cursor:
                                      guests.children <= 0
                                        ? "not-allowed"
                                        : "pointer",
                                    opacity: guests.children <= 0 ? 0.35 : 1,
                                    transition: "opacity 0.2s",
                                  }}
                                  onClick={() =>
                                    setGuests((prev) => ({
                                      ...prev,
                                      children: Math.max(0, prev.children - 1),
                                    }))
                                  }
                                >
                                  <i
                                    className="fas fa-minus"
                                    style={{ fontSize: "10px" }}
                                  ></i>
                                </button>
                                <span
                                  className="fw-semibold"
                                  style={{
                                    minWidth: "24px",
                                    textAlign: "center",
                                    flexShrink: 0,
                                  }}
                                >
                                  {guests.children}
                                </span>
                                <button
                                  type="button"
                                  disabled={
                                    guests.adults + guests.children >=
                                    (propertyInfo?.guests || 99)
                                  }
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    borderRadius: "50%",
                                    border: "1.5px solid #adb5bd",
                                    background: "transparent",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    cursor:
                                      guests.adults + guests.children >=
                                      (propertyInfo?.guests || 99)
                                        ? "not-allowed"
                                        : "pointer",
                                    opacity:
                                      guests.adults + guests.children >=
                                      (propertyInfo?.guests || 99)
                                        ? 0.35
                                        : 1,
                                    transition: "opacity 0.2s",
                                  }}
                                  onClick={() =>
                                    setGuests((prev) => {
                                      const n = prev.children + 1;
                                      return {
                                        ...prev,
                                        children:
                                          n + prev.adults <=
                                          (propertyInfo?.guests || 99)
                                            ? n
                                            : prev.children,
                                      };
                                    })
                                  }
                                >
                                  <i
                                    className="fas fa-plus"
                                    style={{ fontSize: "10px" }}
                                  ></i>
                                </button>
                              </div>
                            </div>

                            {/* Infants */}
                            <div className="d-flex justify-content-between align-items-center mb-3 gap-2">
                              <div className="min-w-0">
                                <div className="fw-semibold font-onest">
                                  Infants
                                </div>
                                <div className="text-muted small font-onest">
                                  Under 2
                                </div>
                              </div>
                              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                <button
                                  type="button"
                                  disabled={guests.infants <= 0}
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    borderRadius: "50%",
                                    border: "1.5px solid #adb5bd",
                                    background: "transparent",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    cursor:
                                      guests.infants <= 0
                                        ? "not-allowed"
                                        : "pointer",
                                    opacity: guests.infants <= 0 ? 0.35 : 1,
                                    transition: "opacity 0.2s",
                                  }}
                                  onClick={() =>
                                    setGuests((prev) => ({
                                      ...prev,
                                      infants: Math.max(0, prev.infants - 1),
                                    }))
                                  }
                                >
                                  <i
                                    className="fas fa-minus"
                                    style={{ fontSize: "10px" }}
                                  ></i>
                                </button>
                                <span
                                  className="fw-semibold"
                                  style={{
                                    minWidth: "24px",
                                    textAlign: "center",
                                    flexShrink: 0,
                                  }}
                                >
                                  {guests.infants}
                                </span>
                                <button
                                  type="button"
                                  disabled={guests.infants >= 5}
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    borderRadius: "50%",
                                    border: "1.5px solid #adb5bd",
                                    background: "transparent",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    cursor:
                                      guests.infants >= 5
                                        ? "not-allowed"
                                        : "pointer",
                                    opacity: guests.infants >= 5 ? 0.35 : 1,
                                    transition: "opacity 0.2s",
                                  }}
                                  onClick={() =>
                                    setGuests((prev) => ({
                                      ...prev,
                                      infants: Math.min(5, prev.infants + 1),
                                    }))
                                  }
                                >
                                  <i
                                    className="fas fa-plus"
                                    style={{ fontSize: "10px" }}
                                  ></i>
                                </button>
                              </div>
                            </div>

                            {/* Pets */}
                            {propertyInfo?.pets > 0 && (
                              <div className="d-flex justify-content-between align-items-center gap-2">
                                <div className="min-w-0">
                                  <div className="fw-semibold font-onest">
                                    Pets
                                  </div>
                                  <div className="text-muted small font-onest">
                                    Max {propertyInfo.pets}
                                  </div>
                                </div>
                                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                  <button
                                    type="button"
                                    disabled={guests.pets <= 0}
                                    style={{
                                      width: "32px",
                                      height: "32px",
                                      borderRadius: "50%",
                                      border: "1.5px solid #adb5bd",
                                      background: "transparent",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      flexShrink: 0,
                                      cursor:
                                        guests.pets <= 0
                                          ? "not-allowed"
                                          : "pointer",
                                      opacity: guests.pets <= 0 ? 0.35 : 1,
                                      transition: "opacity 0.2s",
                                    }}
                                    onClick={() =>
                                      setGuests((prev) => ({
                                        ...prev,
                                        pets: Math.max(0, prev.pets - 1),
                                      }))
                                    }
                                  >
                                    <i
                                      className="fas fa-minus"
                                      style={{ fontSize: "10px" }}
                                    ></i>
                                  </button>
                                  <span
                                    className="fw-semibold font-onest"
                                    style={{
                                      minWidth: "24px",
                                      textAlign: "center",
                                      flexShrink: 0,
                                    }}
                                  >
                                    {guests.pets}
                                  </span>
                                  <button
                                    type="button"
                                    disabled={guests.pets >= propertyInfo.pets}
                                    style={{
                                      width: "32px",
                                      height: "32px",
                                      borderRadius: "50%",
                                      border: "1.5px solid #adb5bd",
                                      background: "transparent",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      flexShrink: 0,
                                      cursor:
                                        guests.pets >= propertyInfo.pets
                                          ? "not-allowed"
                                          : "pointer",
                                      opacity:
                                        guests.pets >= propertyInfo.pets
                                          ? 0.35
                                          : 1,
                                      transition: "opacity 0.2s",
                                    }}
                                    onClick={() =>
                                      setGuests((prev) => ({
                                        ...prev,
                                        pets: Math.min(
                                          propertyInfo.pets,
                                          prev.pets + 1,
                                        ),
                                      }))
                                    }
                                  >
                                    <i
                                      className="fas fa-plus"
                                      style={{ fontSize: "10px" }}
                                    ></i>
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="my-4">
                    <button
                      className="btn btn-primary py-3 w-full rounded-4"
                      disabled={
                        loading ||
                        (!dateFrom && type !== "sale") ||
                        (!dateTo && type !== "sale") ||
                        (dateFrom && dateTo && dateFrom > dateTo) ||
                        (type === "sale" && status === "unavailable") ||
                        (type === "shortlet" &&
                          guests.adults + guests.children === 0)
                      }
                      onClick={async () => {
                        // Check if user is authenticated
                        if (!user || !user?.id) {
                          // Save booking data to sessionStorage before showing login modal
                          const slug =
                            propertyInfo?.slug ||
                            `${(propertyInfo?.title || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${propertyInfo?.referenceNumber || ""}`;

                          const bookingData = {
                            propertyImage: propertyInfo?.coverImage || "",
                            propertyName: propertyInfo?.title || "",
                            propertyRef: propertyInfo?.referenceNumber || "",
                            propertyId: id,
                            rating: propertyInfo?.averageRating || 0,
                            reviewCount: propertyInfo?.reviewsCount || 0,
                            dateFrom: dateFrom,
                            dateTo: dateTo,
                            guests: guests,
                            pricePerNight:
                              type === "shortlet" && isPartyPrice
                                ? Number(partyPrice) || 0
                                : propertyInfo?.hasDailyPrice
                                  ? propertyInfo?.dailyPrice || 0
                                  : price || 0,
                            numberOfDays: normalDays || 1,
                            cautionFee:
                              Number(
                                isPartyPrice ? partyCautionFee : cautionFee,
                              ) || 0,
                            serviceFee: rentPackage.serviceCharge,
                            currency: currency || "₦",
                            instantBooking:
                              propertyInfo?.instantBooking || false,
                            usePartyPrice:
                              type === "shortlet" ? isPartyPrice : false,
                            slug: slug,
                            // Unknown until after login — BookingForm will resolve without flash
                            showGuestProfile: true,
                          };

                          console.log(
                            "PriceCard - Saving booking data to sessionStorage:",
                            bookingData,
                          );
                          sessionStorage.setItem(
                            "pendingBooking",
                            JSON.stringify(bookingData),
                          );
                          console.log("PriceCard - Showing login modal");
                          setShowLoginModal(true);
                          return;
                        }

                        try {
                          setLoading(true);

                          const bookingData = {
                            propertyImage: propertyInfo?.coverImage || "",
                            propertyName: propertyInfo?.title || "",
                            propertyRef: propertyInfo?.referenceNumber || "",
                            propertyId: id,
                            rating: propertyInfo?.averageRating || 0,
                            reviewCount: propertyInfo?.reviewsCount || 0,
                            dateFrom: dateFrom,
                            dateTo: dateTo,
                            guests: guests,
                            pricePerNight:
                              type === "shortlet" && isPartyPrice
                                ? Number(partyPrice) || 0
                                : propertyInfo?.hasDailyPrice
                                  ? propertyInfo?.dailyPrice || 0
                                  : price || 0,
                            numberOfDays: normalDays || 1,
                            cautionFee:
                              Number(
                                isPartyPrice ? partyCautionFee : cautionFee,
                              ) || 0,
                            serviceFee: rentPackage.serviceCharge,
                            currency: currency || "₦",
                            instantBooking:
                              propertyInfo?.instantBooking || false,
                            usePartyPrice:
                              type === "shortlet" ? isPartyPrice : false,
                            showGuestProfile: shouldShowGuestProfileForm(user),
                          };

                          // Save booking data to sessionStorage for non-authenticated users
                          sessionStorage.setItem(
                            "pendingBooking",
                            JSON.stringify(bookingData),
                          );

                          // Construct slug from property title and reference
                          const slug =
                            propertyInfo?.slug ||
                            `${(propertyInfo?.title || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${propertyInfo?.referenceNumber || ""}`;

                          // Navigate to booking screen with slug-based URL
                          navigate(`/property/${slug}/book`, {
                            state: bookingData,
                          });
                        } catch (error) {
                          toast.error(
                            (error as any)?.errors?.[0]?.message ||
                              "Failed to proceed. Please try again.",
                          );
                        } finally {
                          setLoading(false);
                        }
                      }}
                    >
                      {loading ? (
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        ></span>
                      ) : (
                        "Book Now"
                      )}
                    </button>
                    <p className="text-center py-4 fs-12">
                      You won't be charged yet
                    </p>
                  </div>

                  {type === "shortlet" && dateFrom && dateTo ? (
                    <PeakPeriod
                      mainNormalPrice={price}
                      normalDays={normalDays}
                      normalDateRanges={normalDateRanges}
                      normalPrice={normalPrice}
                      // peakDays={peakDays}
                      // peakDateRanges={peakDateRanges}
                      // peakPrice={peakPrice}
                      cautionFee={cautionFee}
                      currency={currency}
                      surgeDays={surgeDays}
                      surgeDateRanges={surgeDateRanges}
                      surgePrice={surgePrice}
                      partyPrice={partyPrice}
                      isPartyPrice={isPartyPrice}
                      partyCautionFee={partyCautionFee}
                    />
                  ) : null}
                  <div className="d-grid my-4 gap-3">
                    {type === "sale" && status === "unavailable" && (
                      <h6 className="text-center text-red">
                        Property is sold out
                      </h6>
                    )}
                  </div>
                  {/* {type === "shortlet" && (
                <div className="flex gap-2 items-center">
                  <i
                    className="fa fa-info-circle text-red"
                    aria-hidden="true"
                  ></i>

                  <p className="text-red fs-12">
                    Coupon will be applied at the point of payment
                  </p>
                </div>
              )} */}
                  {type !== "shortlet" && (
                    <p className="text-secondary fs-12">
                      Requesting a physical tour will notify the person who
                      listed this space. It's free of charge.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {modalType === "login" && (
        <Modal
          show={showModal}
          onClose={() => setShowModal(false)}
          size={400}
          position="top"
        >
          <div className="row px-4 py-1">
            <h5 className="fw-bolder mb-2">Log In to Continue</h5>
            <p className="fs-14 mb-4">
              You need to login or sign up to do this
            </p>
            <div className="d-flex gap-2 justify-content-between mb-3">
              <Link
                to={{ pathname: "/sign-in/", search: "utm_source=modal" }}
                className="btn text-nowrap btn-primary rounded-pill prompt-link"
              >
                Log In
              </Link>
              <Link
                to={{ pathname: "/get-started/", search: "utm_source=modal" }}
                className="btn text-nowrap btn-outline-dark rounded-pill prompt-link fw-bold"
                onClick={handleSignUpClick}
              >
                Sign Up
              </Link>
            </div>
          </div>
        </Modal>
      )}
      {modalType === "kyc" && (
        <Modal
          show={showModal}
          onClose={() => {
            setShowModal(false);
            setModalType("login");
          }}
          size={400}
          position="top"
        >
          <div className="row px-4 py-1">
            <h5 className="fw-bolder mb-2">{`${
              kyc?.status === "PENDING" ? "Pending KYC Approval" : "Pending KYC"
            }`}</h5>
            <p className="fs-14 mb-4">
              {`${
                kyc?.status === "PENDING"
                  ? "Your KYC is pending approval. Please contact Krent on +2349118008008 if you didnt get a response before completing this transaction"
                  : "You need to complete your KYC before your can complete this transaction."
              }`}
            </p>
            <div className="d-flex gap-2 justify-content-between mb-3">
              {kyc?.status !== "PENDING" ? (
                <Link
                  to={{ pathname: "/settings?tab=kyc" }}
                  state={{ from: location.pathname + location.search }}
                  className="btn btn-primary rounded-pill py-2 px-3"
                >
                  Add Now
                </Link>
              ) : (
                <div></div>
              )}
              <button
                onClick={() => {
                  setModalType("login");
                  setShowModal(false);
                  bookTour();
                }}
                className="btn btn-outline-dark rounded-pill py-2 px-3 fw-bold"
              >
                Continue
              </button>
            </div>
          </div>
        </Modal>
      )}

      {modalType === "tour" && (
        <Modal
          key="tour-modal"
          show={showModal}
          onClose={() => {
            setShowModal(false);
            setTimeout(() => {
              setModalType("login");
            }, 500);
          }}
          size={900}
          position="top"
        >
          <iframe
            title="Virtual Tour"
            src={virtualTour}
            width="100%"
            height="700px"
            allowFullScreen
          ></iframe>
        </Modal>
      )}

      <LoginModal
        show={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onOpenSignup={() => {
          setShowLoginModal(false);
          setShowSignupModal(true);
        }}
        onLoginSuccess={() => {
          setShowLoginModal(false);
        }}
      />

      <GetStartedModal
        show={showSignupModal}
        onClose={() => setShowSignupModal(false)}
        onOpenLogin={() => {
          setShowSignupModal(false);
          setShowLoginModal(true);
        }}
        initialStep="signup"
        initialAccountType="buyer"
        hideBackButton={true}
      />

      {/* ── Book Inspection Modal ───────────────────────────────── */}
      <Modal
        show={showInspectionModal}
        onClose={() => setShowInspectionModal(false)}
        size={500}
      >
        <div className="px-4 pb-5 pt-3 text-center">
          {/* Calendly icon in teal circle */}
          <div
            className="mx-auto mb-4 d-flex align-items-center justify-content-center rounded-circle"
            style={{ width: "80px", height: "80px", background: "#e6f4f1" }}
          >
            <img
              src={CalendlyIcon}
              alt="Calendly"
              style={{ width: "48px", height: "48px" }}
            />
          </div>

          <h5 className="fw-bold font-onest mb-2" style={{ fontSize: "20px" }}>
            Schedule Your Property Inspection
          </h5>
          <p
            className="font-onest mb-4"
            style={{ color: "#6b7280", fontSize: "14px", lineHeight: "1.6" }}
          >
            You're about to book an in-person inspection. You'll be taken to our
            Calendly page to select a time that works for you.
          </p>

          <div
            className="rounded-3 p-4 mb-4 text-start"
            style={{ border: "1.5px solid #47BBA9", background: "#fafffe" }}
          >
            <h6
              className="fw-bold font-onest text-center mb-3"
              style={{ color: "#47BBA9", fontSize: "16px" }}
            >
              How It Works
            </h6>

            <div className="d-flex align-items-start gap-3 mb-3">
              <i
                className="fa-regular fa-calendar-days"
                style={{
                  color: "#47BBA9",
                  fontSize: "18px",
                  flexShrink: 0,
                  marginTop: "2px",
                }}
              ></i>
              <p
                className="mb-0 font-onest"
                style={{ fontSize: "14px", color: "#374151" }}
              >
                <strong>Pick Your Slot:</strong> Choose your preferred date and
                time on Calendly.
              </p>
            </div>

            <div className="d-flex align-items-start gap-3 mb-3">
              <i
                className="fa-regular fa-envelope"
                style={{
                  color: "#47BBA9",
                  fontSize: "18px",
                  flexShrink: 0,
                  marginTop: "2px",
                }}
              ></i>
              <p
                className="mb-0 font-onest"
                style={{ fontSize: "14px", color: "#374151" }}
              >
                <strong>Check Your Inbox:</strong> Use your active email for
                confirmation details.
              </p>
            </div>

            <div className="d-flex align-items-start gap-3">
              <i
                className="fa-solid fa-phone"
                style={{
                  color: "#47BBA9",
                  fontSize: "18px",
                  flexShrink: 0,
                  marginTop: "2px",
                }}
              ></i>
              <p
                className="mb-0 font-onest"
                style={{ fontSize: "14px", color: "#374151" }}
              >
                <strong>Our Team Will Reach Out:</strong> A member of our
                Customer Success team will contact you on your chosen date to
                confirm the visit.
              </p>
            </div>
          </div>

          <button
            type="button"
            className="btn py-3 w-100 fw-semibold font-onest rounded-4"
            style={{
              background: "#47BBA9",
              color: "#fff",
              border: "none",
              fontSize: "16px",
            }}
            onClick={() => {
              setShowInspectionModal(false);
              window.open(
                "https://calendly.com/krentoperations/house-inspection",
                "_blank",
              );
            }}
          >
            Proceed to Calendly
          </button>
        </div>
      </Modal>

      {paymentBill && (
        <PaymentMethodModal
          show={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          bookingId={rentBookingId}
          referenceId={rentReferenceId}
          totalAmount={Number(paymentBill.total)}
          currency={paymentBill.currency || "NGN"}
          startRentBooking={rentBookingId ? undefined : startRentBooking}
        />
      )}
    </React.Fragment>
  );
};
